import { TrendingUp } from 'lucide-react';
import type { UpliftPosition, UpliftSubScore } from '../../types/calculations';

interface UpliftScorecardProps {
  uplift: UpliftPosition;
}

interface SubScoreCardProps {
  label: string;
  subscore: UpliftSubScore;
}

// Human-readable labels for raw backend component keys
const COMPONENT_LABELS: Record<string, { label: string; format: 'score' | 'percent' | 'count' | 'text' }> = {
  zandi_rent_gap:        { label: 'Rent Gap Score',        format: 'score' },
  egi_cagr_5yr:          { label: '5yr Income Growth',     format: 'percent' },
  egi_score:             { label: 'Income Growth Score',   format: 'score' },
  rent_spread:           { label: 'Rent Spread',           format: 'percent' },
  rent_spread_score:     { label: 'Rent Spread Score',     format: 'score' },
  rent_uplift_potential: { label: 'Uplift Potential',      format: 'percent' },
  zandi_momentum:        { label: 'Market Momentum',       format: 'score' },
  zandi_valuation:       { label: 'Valuation Score',       format: 'score' },
  cap_compression_bps:   { label: 'Cap Compression',       format: 'score' },
  cap_compression_score: { label: 'Compression Score',     format: 'score' },
  yield_on_cost:         { label: 'Yield on Cost',         format: 'percent' },
  zandi_vacancy:         { label: 'Vacancy Score',         format: 'score' },
  zandi_permit:          { label: 'Permit Score',          format: 'score' },
  recent_permits:        { label: 'Recent Permits',        format: 'count' },
  permit_score:          { label: 'Supply Pressure',       format: 'score' },
  zandi_mobi:            { label: 'Market Outperformance', format: 'score' },
  zandi_composite:       { label: 'Zandi Composite',       format: 'score' },
  demographic_score:     { label: 'Demographics',          format: 'score' },
  trajectory:            { label: 'Neighborhood Trend',    format: 'text' },
};

/**
 * Generate a plain-English one-liner for each sub-score category.
 */
function getSubScoreSummary(label: string, subscore: UpliftSubScore): string {
  const grade = subscore.score >= 70 ? 'Strong' : subscore.score >= 50 ? 'Moderate' : 'Weak';
  const components = subscore.components;

  if (label.startsWith('Rent Uplift')) {
    const spread = components.rent_spread;
    if (typeof spread === 'number') {
      return `${grade} rent growth potential — current rents are ${(spread * 100).toFixed(0)}% below market`;
    }
    return `${grade} rent growth potential based on submarket rent gap analysis`;
  }

  if (label.startsWith('Value Uplift')) {
    const bps = components.cap_compression_bps;
    if (typeof bps === 'number' && bps > 0) {
      return `${grade} value appreciation — ${Math.abs(bps).toFixed(0)}bp cap compression expected at exit`;
    }
    return `${grade} property value appreciation based on market momentum`;
  }

  if (label.startsWith('Supply/Demand')) {
    const vacScore = components.zandi_vacancy;
    if (typeof vacScore === 'number') {
      const tightness = vacScore >= 80 ? 'very tight' : vacScore >= 60 ? 'healthy' : 'soft';
      return `${grade} supply/demand balance — rental market is ${tightness}`;
    }
    return `${grade} supply/demand dynamics based on vacancy and permit trends`;
  }

  if (label.startsWith('Competitive')) {
    const traj = components.trajectory;
    if (typeof traj === 'string') {
      return `${grade} competitive position in a ${traj} neighborhood`;
    }
    return `${grade} competitive positioning relative to comparable submarkets`;
  }

  return `${grade} outlook based on macro and micro market signals`;
}

function getGradeColor(score: number): { text: string; bg: string; border: string } {
  if (score >= 70) return { text: 'text-emerald', bg: 'bg-emerald/10', border: 'border-emerald/30' };
  if (score >= 50) return { text: 'text-gold', bg: 'bg-gold/10', border: 'border-gold/30' };
  return { text: 'text-crimson', bg: 'bg-crimson/10', border: 'border-crimson/30' };
}

function SubScoreCard({ label, subscore }: SubScoreCardProps) {
  const colors = getGradeColor(subscore.score);

  return (
    <div className="rounded-lg border border-slate-dark bg-charcoal p-4">
      {/* Header Row */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="text-sm uppercase tracking-[0.12em] text-gold mb-1">{label}</div>
          <div className="flex items-center gap-2">
            <span className={`inline-block px-2 py-0.5 rounded text-sm font-bold ${colors.bg} ${colors.text} ${colors.border} border`}>
              {subscore.grade}
            </span>
            <span className="text-sm font-mono text-cream">{subscore.score.toFixed(0)}/100</span>
          </div>
        </div>
      </div>

      {/* Plain-English Summary */}
      <p className="text-sm text-ivory/70 leading-relaxed mb-3">
        {getSubScoreSummary(label, subscore)}
      </p>

      {/* Component Breakdown */}
      <div className="border-t border-slate-dark pt-3 space-y-1">
        {Object.entries(subscore.components).map(([name, value]) => {
          const meta = COMPONENT_LABELS[name];
          const displayLabel = meta?.label || name.replace(/_/g, ' ');
          const formatType = meta?.format || 'score';

          let displayValue: string;
          if (typeof value === 'string') {
            displayValue = value;
          } else if (formatType === 'percent') {
            displayValue = `${(value * 100).toFixed(1)}%`;
          } else if (formatType === 'count') {
            displayValue = String(Math.round(value));
          } else {
            displayValue = value.toFixed(1);
          }

          return (
            <div key={name} className="flex justify-between items-center text-sm">
              <span className="text-stone-700 capitalize">
                {displayLabel}
              </span>
              <span className="font-mono text-cream">
                {displayValue}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function UpliftScorecard({ uplift }: UpliftScorecardProps) {
  const compositeColors = getGradeColor(uplift.composite_score);

  return (
    <div className="space-y-4">
      {/* Composite Score Hero */}
      <div className="rounded-lg border-2 border-gold bg-gradient-to-br from-charcoal to-graphite p-6">
        <div className="flex items-center justify-between">
          {/* Left Side - Title & Confidence */}
          <div className="flex items-start gap-3">
            <div className="mt-1">
              <TrendingUp className="w-6 h-6 text-gold" />
            </div>
            <div>
              <h3 className="text-lg uppercase tracking-[0.12em] text-gold font-semibold mb-1">
                Uplift Position
              </h3>
              <p className="text-sm text-stone-700 capitalize">
                {uplift.confidence} confidence
              </p>
            </div>
          </div>

          {/* Right Side - Grade & Score */}
          <div className="text-right">
            <div className={`text-4xl font-mono font-bold ${compositeColors.text} mb-1`}>
              {uplift.letter_grade}
            </div>
            <div className="text-sm font-mono text-stone-700">
              {uplift.composite_score.toFixed(0)}/100
            </div>
          </div>
        </div>
      </div>

      {/* Sub-Score Grid (2x2) */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SubScoreCard label="Rent Uplift (35%)" subscore={uplift.rent_uplift} />
        <SubScoreCard label="Value Uplift (30%)" subscore={uplift.value_uplift} />
        <SubScoreCard label="Supply/Demand (20%)" subscore={uplift.supply_demand} />
        <SubScoreCard label="Competitive Position (15%)" subscore={uplift.competitive_position} />
      </div>

      {/* Data Provenance Footer */}
      <div className="text-sm text-stone-700 space-y-1 px-2">
        <div>
          <span className="font-semibold">Macro sources:</span>{' '}
          {uplift.macro_sources.join(', ') || 'None'}
        </div>
        <div>
          <span className="font-semibold">Micro sources:</span>{' '}
          {uplift.micro_sources.join(', ') || 'None'}
        </div>
      </div>
    </div>
  );
}
